import { useEffect, useState } from "react";
import axios from "axios";

// "Sign in with your Wallet key". No passwords, no email: the FitKey pass link
// (or a pairing code from another device) is exchanged for a session token,
// kept on this device only. Signing out just forgets the token locally.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "fittin_session";
const CHANGE_EVENT = "fittin-session-change";
export const UNAUTHORIZED_EVENT = "fittin-session-unauthorized";

export function getSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s?.token || !s?.fitkey_id) return null;
    if (s.expires_at && s.expires_at * 1000 < Date.now()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch (_e) {
    return null;
  }
}

function emit() {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch (_e) {
    /* noop */
  }
}

export function setSession({ token, fitkey_id, expires_at }) {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ token, fitkey_id, expires_at }));
  } catch (_e) {
    /* storage unavailable */
  }
  emit();
}

let userSignOutAt = 0;
// True right after the user signed out themselves (vs. an expired session), so
// private pages can send them home instead of to /signin.
export const justSignedOut = () => Date.now() - userSignOutAt < 3000;

export function clearSession({ userInitiated = false } = {}) {
  if (userInitiated) userSignOutAt = Date.now();
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch (_e) {
    /* noop */
  }
  emit();
}

// React hook: current session, re-renders on sign in / out (also across tabs).
export function useSession() {
  const [session, setState] = useState(() => getSession());
  useEffect(() => {
    const sync = () => setState(getSession());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return session;
}

// POST /api/session with {fitkey_id, access_key} or {code}.
// Resolves { ok, session } or { ok:false, status }. Never logs the secret.
export async function startSession(body) {
  try {
    const r = await axios.post(`${API}/session`, body, { timeout: 12000 });
    const d = r?.data || {};
    if (!d.token || !d.fitkey_id) return { ok: false, status: 0 };
    setSession(d);
    return { ok: true, session: d };
  } catch (e) {
    return { ok: false, status: e?.response?.status || 0 };
  }
}

// Authenticated request. On 401 the token is dropped and the app is sent to
// /signin (see AuthRedirector in App.js). Throws on any non-2xx.
export async function authRequest(method, path, data) {
  const s = getSession();
  if (!s) {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    const err = new Error("Not signed in");
    err.status = 401;
    throw err;
  }
  try {
    const r = await axios({
      method,
      url: `${API}${path}`,
      data,
      timeout: 12000,
      headers: { Authorization: `Bearer ${s.token}` },
    });
    return r.data;
  } catch (e) {
    const status = e?.response?.status || 0;
    if (status === 401) {
      clearSession();
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    const err = new Error(e?.response?.data?.detail || "Request failed");
    err.status = status;
    throw err;
  }
}

// "ABCDEFGH" / "abcd efgh" -> "ABCD-EFGH" (as the user types).
export function formatPairCode(text) {
  const clean = String(text || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8);
  return clean.length > 4 ? `${clean.slice(0, 4)}-${clean.slice(4)}` : clean;
}

// Which wallet the device can add: android | ios | desktop.
export function detectPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "ios"; // iPadOS
  return "desktop";
}
